import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ImagePlus } from 'lucide-react';
export function SolutionSection() {
  const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const [images, setImages] = useState<(string | null)[]>([null, null, null]);
  const handleImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImages(prev => {
      const next = [...prev];
      next[index] = url;
      return next;
    });
  };
  return <section className="pt-12 pb-10 bg-orange-50 text-gray-900 relative overflow-hidden" data-id="element-324">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-40 -z-10" data-id="element-325" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-violet-200 rounded-full blur-3xl opacity-40 -z-10" data-id="element-326" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-327">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-328">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-violet-600 font-extrabold tracking-wider uppercase text-2xl md:text-4xl mb-4 block" data-id="element-329">
            LA SOLUCIÓN
          </motion.span>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-4xl md:text-6xl font-extrabold mb-4 leading-tight" data-id="element-330">
            <span className="bg-gradient-to-r from-orange-500 via-violet-600 to-emerald-500 bg-clip-text text-transparent" data-id="element-331">
              Recetas Mágicas para Niños que Odian las Verduras
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg text-gray-600 font-medium" data-id="element-332">
            El recetario que te resuelve la comida de todos los dias 🥦✨
          </motion.p>
        </div>

        {/* Book cover image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex justify-center mb-10" data-id="element-333">
          <img src="https://cdn.magicpatterns.com/uploads/pmHM5xA3wpfURMsoU4Yd1k/WhatsApp_Image_2026-02-24_at_20.15.04.jpg" alt="Recetas Mágicas para Niños que Odian las Verduras - portada del ebook" className="w-auto h-auto max-w-sm sm:max-w-md md:max-w-lg block" data-id="element-334" />
        </motion.div>

        {/* Description text */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-2xl mx-auto mb-12 text-center" data-id="element-335">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-medium" data-id="element-336">
            Nuestro ebook te enseñará{' '}
            <span className="font-extrabold text-violet-600" data-id="element-337">
              página por página
            </span>{' '}
            recetas divertidas y rápidas, con{' '}
            <span className="font-extrabold text-orange-500" data-id="element-338">
              tips para compartir con tu nene
            </span>{' '}
            mientras cocinás, de modo que se vaya familiarizando con las{' '}
            <span className="font-extrabold text-emerald-600" data-id="element-339">
              verduras de colores
            </span>{' '}
            🌈🥕🥦. Estas son algunas de nuestras recetas:
          </p>
        </motion.div>

        {/* Product image upload slots (3) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-340">
          {/* Recipe card 1 - Croquetas */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-orange-100" data-id="element-341">
            <div className="relative h-56 overflow-hidden" data-id="element-342">
              <img src="https://cdn.magicpatterns.com/uploads/uzF48y1otsauSm8n1zkw2T/Captura_de_pantalla_2026-02-24_a_las_8.30.52_p._m..png" alt="Croquetas de Calabaza y Brócoli" className="w-full h-full object-cover" data-id="element-343" />
            </div>
            <div className="p-5" data-id="element-344">
              <h3 className="text-lg font-extrabold text-violet-700 leading-tight mb-1" data-id="element-345">
                Croquetas de Calabaza y Brócoli
              </h3>
              <p className="text-sm font-semibold text-orange-500 mb-2" data-id="element-346">
                ¡Croquetas coloridas con dos verduras escondidas!
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-500 font-medium" data-id="element-347">
                <span data-id="element-348">⏱ 40 min</span>
                <span data-id="element-349">🥦 Dificultad: Media</span>
              </div>
            </div>
          </motion.div>

          {/* Recipe card 2 - Muffins */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-orange-100" data-id="element-350">
            <div className="relative h-56 overflow-hidden" data-id="element-351">
              <img src="https://cdn.magicpatterns.com/uploads/6jcoiGoskzfYicbX1qp6F2/Imagen_24-2-26_a_las_8.35_p._m..jpg" alt="Muffins de Espinacas y Tomate" className="w-full h-full object-cover" data-id="element-352" />
            </div>
            <div className="p-5" data-id="element-353">
              <h3 className="text-lg font-extrabold text-violet-700 leading-tight mb-1" data-id="element-354">
                Muffins de Espinacas y Tomate
              </h3>
              <p className="text-sm font-semibold text-orange-500 mb-2" data-id="element-355">
                ¡Magdalenas saladas con espinaca y tomate escondidos!
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-500 font-medium" data-id="element-356">
                <span data-id="element-357">⏱ 25 min</span>
                <span data-id="element-358">🌿 Dificultad: Fácil</span>
              </div>
            </div>
          </motion.div>

          {/* Upload slot 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-orange-100" data-id="element-359">
            <div className="relative h-56 overflow-hidden" data-id="element-360">
              <img src="https://cdn.magicpatterns.com/uploads/usf9b1S7NMJZ8qQhiHP4jZ/Imagen_24-2-26_a_las_8.40_p._m..jpg" alt="Pasta Integral con Salsa de Calabaza" className="w-full h-full object-cover" data-id="element-361" />
            </div>
            <div className="p-5" data-id="element-362">
              <h3 className="text-lg font-extrabold text-violet-700 leading-tight mb-1" data-id="element-363">
                Pasta Integral con Salsa de Calabaza
              </h3>
              <p className="text-sm font-semibold text-orange-500 mb-2" data-id="element-364">
                ¡Pasta saludable con una salsa naranja mágica!
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-500 font-medium" data-id="element-365">
                <span data-id="element-366">⏱ 35 min</span>
                <span data-id="element-367">🎃 Dificultad: Fácil</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom text */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-2xl mx-auto mt-10 text-center bg-white/60 rounded-2xl px-8 py-6 shadow-md border border-orange-100" data-id="element-368">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium" data-id="element-369">
            🎨 El ebook trae{' '}
            <span className="font-extrabold text-violet-600" data-id="element-370">
              hermosas ilustraciones
            </span>{' '}
            y tips para hacer como{' '}
            <span className="font-extrabold text-orange-500" data-id="element-371">
              actividad con tu nene
            </span>{' '}
            mientras vos cocinás.
          </p>
        </motion.div>
      </div>
    </section>;
}